import { Subscription, FirestoreSnapper, FirestoreSnapperType, FirestoreQuerySnapper, FirestoreDocSnapper } from './types'
import { AppStore } from '.'
import Vue from 'vue'
import { MVar } from '@/helpers/async_helpers'

type ErrorCallback = ((error: any) => void) | ((error: any) => Promise<void>)

class StoreHelper {
  store: AppStore
  subscriptions: {[key: string]: Subscription}
  constructor (store: AppStore) {
    this.store = store
    this.subscriptions = {}
  }
  async subscribe (key: string, snapper: FirestoreSnapper,
                   errorCallBack?: ErrorCallback,
                   waitForIt: boolean = false) {
    if (this.subscriptions[key] === undefined) {
      this.subscriptions[key] = {
        count: 0,
        destroy: null,
        snapper: null
      }
    }
    if (this.subscriptions[key].count === 0) {
      const mvar = new MVar<void>()
      //TODO: don't branch on type.. (template FirestoreSnapper<Query|Doc>)
      if (snapper.type === FirestoreSnapperType.Query) {
        const snap = snapper as FirestoreQuerySnapper
        this.subscriptions[key].destroy = snap.query.onSnapshot(data => {
          snap.mutation(data)
          if (waitForIt) mvar.put()
        }, error => {
          if (waitForIt) mvar.put()
          if (errorCallBack) {
            errorCallBack(error)
          } else {
            console.error(`Error subscribing ${key} : ${error}`)
          }
        })
      } else if (snapper.type === FirestoreSnapperType.Document) {
        const snap = snapper as FirestoreDocSnapper
        this.subscriptions[key].destroy = snap.doc.onSnapshot(data => {
          snap.mutation(data)
          mvar.put()
        }, error => {
          mvar.put()
          if (errorCallBack) {
            errorCallBack(error)
          } else {
            console.error(`Error subscribing ${key} : ${error}`)
          }
        })
      }
      this.subscriptions[key].snapper = snapper
      if (waitForIt) {
        await mvar.read()
      }
    }
    this.subscriptions[key].count += 1
    return { key, count: this.subscriptions[key].count }
  }
  unsubscribe (key: string, mutation?: () => void) {
    if (!this.subscriptions[key]?.count) {
      console.warn(`You should not unsubscribe if you have not yet subscribed to ${key}`)
      return
    } else {
      this.subscriptions[key].count -= 1
      const count = this.subscriptions[key].count
      if (count === 0) {
        const destroyFunction = this.subscriptions[key].destroy
        if (destroyFunction !== null) {
          destroyFunction()
          if (mutation) {
            mutation()
          }
          delete this.subscriptions[key]
        }
      }
      return { key, count }
    }
  }
  updateQuery (key: string, query: firebase.firestore.Query<firebase.firestore.DocumentData>) {
    const subscription = this.subscriptions[key]
    if (subscription === undefined) {
      throw new Error('Cannot update query of non-existing subscription')
    }
    if (subscription.snapper?.type !== FirestoreSnapperType.Query) {
      throw new Error('Can only update query subscriptions')
    }
    if (subscription.destroy === null) {
      throw new Error('Subscription was not properly created')
    }
    subscription.destroy()
    const snapper = subscription.snapper as FirestoreQuerySnapper
    subscription.destroy = query.onSnapshot(data => snapper.mutation(data), error => {
      throw new Error(`Error subscribing ${key} : ${error}`)
    })
  }
  exists (key: string): boolean {
    return this.subscriptions[key] && this.subscriptions[key].count > 0
  }
  runningKeys (prefix: string = '') {
    return Object.keys(this.subscriptions).filter(key => key.startsWith(prefix))
  }
  compareObjects (obj1: any, obj2: any): boolean {
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
      return obj1 === obj2
    }
    for (const p in obj1) {
      // Check property exists on both objects
      if (obj1.hasOwnProperty(p) !== obj2.hasOwnProperty(p)) {
        return false
      } else {
        switch (typeof (obj1[p])) {
          // Deep compare objects
          case 'object':
            if (!this.compareObjects(obj1[p], obj2[p])) {
              return false
            }
            break
            // Compare function code
          default:
            if (obj1[p] !== obj2[p]) {
              return false
            }
        }
      }
    }
    if (typeof obj1 === 'object' && typeof obj2 === 'object') {
      return this.__secondHasExtraProperties(obj1, obj2)
    }
    return true
  }
  __secondHasExtraProperties (obj1: any, obj2: any): boolean {
    for (const p in obj2) {
      if (typeof (obj1[p]) === 'undefined') {
        return false
      }
    }
    return true
  }
}

function lazyUpdate (state: any, key: string|number, newVal: any) {
  try {
    if (typeof newVal === 'object' && newVal !== null) {
      if (typeof newVal !== typeof state[key]) {
        Vue.set(state, key, newVal)
      } else {
        for (const rightkey in newVal) {
          if (state[key][rightkey] === undefined) {
            Vue.set(state[key], rightkey, newVal[rightkey])
          } else {
            lazyUpdate(state[key], rightkey, newVal[rightkey])
          }
        }
        for (const leftkey in state[key]) {
          if (newVal[leftkey] === undefined) {
            Vue.delete(state[key], leftkey)
          }
        }
      }
    } else if (state[key] !== newVal) {
      Vue.set(state, key, newVal)
    }
  } catch(error) {
    console.error('lazyUpdate', state, key, newVal, error)
  }

}

export default StoreHelper
export { lazyUpdate }
